import { Switch } from "@headlessui/react";

import classNames from "../../utils/classNames";
import { WizardState } from "./types";

interface DropOffStepProps {
  state: WizardState;
  onChange: (values: Pick<WizardState, "doesDropOff" | "dropOffPricePerTonne">) => void;
}

export default function DropOffStep({ state, onChange }: DropOffStepProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Do you accept drop-offs?</h2>
        <p className="mt-1 text-sm text-gray-600">
          If customers can bring their vehicle to your yard themselves, we'll offer a drop-off quote alongside
          collection.
        </p>
      </div>

      <Switch.Group as="div" className="flex items-center gap-4">
        <Switch
          checked={state.doesDropOff}
          onChange={(doesDropOff: boolean) => onChange({ doesDropOff, dropOffPricePerTonne: state.dropOffPricePerTonne })}
          className={classNames(
            state.doesDropOff ? "bg-primary-600" : "bg-gray-200",
            "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          )}
        >
          <span
            aria-hidden="true"
            className={classNames(
              state.doesDropOff ? "translate-x-5" : "translate-x-0",
              "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            )}
          />
        </Switch>
        <Switch.Label className="text-sm font-medium text-gray-900">
          {state.doesDropOff ? "Yes, customers can drop off at my yard" : "No, collection only"}
        </Switch.Label>
      </Switch.Group>

      {state.doesDropOff && (
        <div className="max-w-xs">
          <label htmlFor="dropOffPricePerTonne" className="block text-sm font-medium text-gray-700">
            Drop-off price per tonne (£)
          </label>
          <div className="relative mt-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500">£</span>
            <input
              id="dropOffPricePerTonne"
              type="number"
              min={0}
              step={1}
              value={state.dropOffPricePerTonne ?? ""}
              onChange={(e) =>
                onChange({
                  doesDropOff: true,
                  dropOffPricePerTonne: e.target.value === "" ? null : Math.max(0, +e.target.value),
                })
              }
              className="block w-full rounded-md border-gray-300 pl-7 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="e.g. 150"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Leave blank to use your collection prices for drop-offs too.
          </p>
        </div>
      )}
    </div>
  );
}
